/**
 * PreMeetingBlueprintPage — generation form + rendered Blueprint + chat surface
 * with quick-button preset prompts (locked Q5 chip pattern: tier-gated chips
 * render greyed-out with HR TIER label).
 */

import { useState } from "react";
import { useSession } from "../lib/useSession.js";
import {
  Wordmark, SectionHeader, Callout, SeriesBanner, AuditFooter,
  MarkdownV2, Chip, Composer, ChatTurn, PrintToPdfButton, RecentBlueprintsPanel,
} from "../components/index.js";
import MentionAutocomplete from "../components/MentionAutocomplete.jsx";
import PostMeetingNotes from "../components/PostMeetingNotes.jsx";
import StreamingMarkdown from "../components/StreamingMarkdown.jsx";
import { canAccess, tierDisplayLabel, FEATURE_MIN_TIER } from "../lib/feature-gates.js";
import { useStreamingBlueprint } from "../lib/use-streaming-blueprint.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

const MEETING_TYPES = [
  { value: "1on1_recurring",      label: "1:1 (regular cadence)" },
  { value: "sales_pitch",         label: "Sales pitch / proposal" },
  { value: "negotiation",         label: "Negotiation" },
  { value: "client_meeting",      label: "Client / account meeting" },
  { value: "advisory_session",    label: "Advisory session" },
  { value: "review_checkin",      label: "Review / check-in" },
  { value: "kickoff",             label: "Kickoff / first meeting" },
  { value: "conflict_mediation",  label: "Conflict mediation" },
  { value: "skip_level",          label: "Cross-level meeting" },
  { value: "sensitive_feedback",  label: "Sensitive feedback" },
  { value: "difficult_news",      label: "Difficult news" },
  { value: "other",               label: "Other (free text)" },
];

export default function PreMeetingBlueprintPage() {
  const { session, appUser } = useSession();
  const [meetingType, setMeetingType] = useState("1on1_recurring");
  const [attendeesText, setAttendeesText] = useState("");
  const [attendeeMentions, setAttendeeMentions] = useState([]);
  const [trigger, setTrigger] = useState("");
  const [tone, setTone] = useState("balanced");
  const [chatTurns, setChatTurns] = useState([]);

  const stream = useStreamingBlueprint({
    syncUrl: "/.netlify/functions/pre-meeting",
    streamUrl: "/api/pre-meeting/stream",
    bearerToken: session?.access_token,
  });
  const { streaming, setStreaming, generating, streamRequest, blueprint, error, submit, onStreamDone, reset } = stream;
  const [localError, setLocalError] = useState(null);

  const generate = async () => {
    if (attendeeMentions.length === 0) {
      setLocalError("Mention at least one attendee with @ to ground the Blueprint.");
      return;
    }
    setLocalError(null);
    submit({
      meeting: {
        meeting_type: meetingType,
        scheduled_at_iso: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
        duration_minutes: 30,
        trigger,
        is_sensitive: ["layoff", "pip", "disciplinary"].includes(meetingType),
      },
      attendee_profile_ids: attendeeMentions.map((m) => m.profile_id),
      length: "full",
      tone,
    });
  };

  const askPreset = async (prompt) => {
    if (!blueprint?.blueprint_id) return;
    setChatTurns((turns) => [...turns, { role: "user", content: prompt, ts: new Date() }]);
    try {
      const res = await fetch("/.netlify/functions/blueprint-chat", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          blueprint_id: blueprint.blueprint_id,
          user_message: prompt,
          mode: "pre_meeting",
        }),
      });
      const body = await res.json();
      setChatTurns((turns) => [...turns, {
        role: "assistant",
        content: res.ok ? body.content : `Chat error: ${body.error || "unknown"}`,
        ts: new Date(),
      }]);
    } catch (e) {
      setChatTurns((turns) => [...turns, {
        role: "assistant",
        content: `Network error: ${e.message}`,
        ts: new Date(),
      }]);
    }
  };

  if (!session) return <p style={{ padding: 24 }}>Sign in to generate a Pre-Meeting Blueprint.</p>;

  const tier = appUser?.tier || "manager";

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Wordmark scale={1.0} />
      </div>

      {streamRequest && !blueprint ? (
        <StreamingMarkdown
          url={stream.streamUrl}
          bearerToken={session.access_token}
          body={streamRequest}
          onDone={onStreamDone}
          onRetry={stream.retry}
        />
      ) : !blueprint ? (
        <>
        <RecentBlueprintsPanel
          type="pre_meeting"
          bearerToken={session.access_token}
        />
        <GenerationForm
          meetingType={meetingType}
          setMeetingType={setMeetingType}
          attendeesText={attendeesText}
          setAttendeesText={setAttendeesText}
          attendeeMentions={attendeeMentions}
          setAttendeeMentions={setAttendeeMentions}
          trigger={trigger}
          setTrigger={setTrigger}
          tone={tone}
          setTone={setTone}
          generating={generating}
          streaming={streaming}
          setStreaming={setStreaming}
          onSubmit={generate}
          error={localError || error}
          tier={tier}
          bearerToken={session.access_token}
        />
        </>
      ) : (
        <RenderedBlueprint
          blueprint={blueprint}
          chatTurns={chatTurns}
          onPreset={askPreset}
          onMessage={(text) => askPreset(text)}
          tier={tier}
          attendeeProfileIds={attendeeMentions.map((m) => m.profile_id)}
          attendeeFirstNames={attendeeMentions.map((m) => m.display_name).filter(Boolean)}
          bearerToken={session.access_token}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function GenerationForm({
  meetingType, setMeetingType,
  attendeesText, setAttendeesText, attendeeMentions, setAttendeeMentions,
  trigger, setTrigger, tone, setTone,
  generating, streaming, setStreaming, onSubmit, error, tier, bearerToken,
}) {
  return (
    <div>
      <SectionHeader index={1} title="New Pre-Meeting Blueprint" />

      <Callout.Tip>
        Mention attendees with <code>@</code> to ground the Blueprint in their actual Profile data. The chat is grounded in Profiles, not generic LLM context.
      </Callout.Tip>

      <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
        <Field label="Meeting type">
          <select
            value={meetingType}
            onChange={(e) => setMeetingType(e.target.value)}
            style={selectStyle}
          >
            {MEETING_TYPES.map((mt) => {
              const locked = mt.tier_required && !canAccess(tier, `pre_meeting_${mt.value}`);
              return (
                <option key={mt.value} value={mt.value} disabled={locked}>
                  {mt.label}{locked ? ` — ${tierDisplayLabel(mt.tier_required)} required` : ""}
                </option>
              );
            })}
          </select>
        </Field>

        <Field label="Attendees (mention with @)">
          <MentionAutocomplete
            value={attendeesText}
            onChange={setAttendeesText}
            mentions={attendeeMentions}
            onMentionsChange={setAttendeeMentions}
            placeholder="@Sarah @Marcus"
            rows={2}
            bearerToken={bearerToken}
          />
        </Field>

        <Field label="Trigger / context (optional)">
          <textarea
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            placeholder="What prompted this meeting? Recent thread, incident, performance signal…"
            rows={3}
            style={inputStyle}
          />
        </Field>

        <Field label="Tone">
          <div style={{ display: "flex", gap: 8 }}>
            {["hard", "balanced", "soft"].map((t) => (
              <Chip key={t} primary={tone === t} onClick={() => setTone(t)}>{t}</Chip>
            ))}
          </div>
        </Field>

        {error && (
          <div
            style={{
              padding: "12px 14px",
              border: "2px solid var(--ppr-color-accent-coral)",
              background: "var(--ppr-color-accent-coral-very-soft)",
              color: "var(--ppr-color-accent-coral-dark)",
            }}
          >
            {error}
          </div>
        )}

        <button
          onClick={onSubmit}
          disabled={generating}
          style={generating ? terminalActionButtonDisabledStyle : terminalActionButtonStyle}
        >
          {generating ? "Generating Blueprint…" : "Generate Blueprint"}
        </button>
      </div>
    </div>
  );
}

function RenderedBlueprint({ blueprint, chatTurns, onPreset, onMessage, tier, attendeeProfileIds, attendeeFirstNames, bearerToken }) {
  return (
    <article>
      <SeriesBanner type="Pre-Meeting Blueprint" dateLabel={new Date().toISOString().slice(0, 10)} />

      <div style={{ marginTop: 28 }}>
        <h1
          style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-4xl)",
            color: "var(--ppr-color-accent-navy)",
            letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
            lineHeight: "var(--ppr-typography-line-height-tight)",
            margin: "0 0 8px",
          }}
        >
          {blueprint.title}
        </h1>
        {blueprint.power_asymmetry_rendered && (
          <Callout.WatchOut>
            <strong>Power asymmetry:</strong> {blueprint.trap_headline || "You hold structural power in this conversation."}
          </Callout.WatchOut>
        )}
      </div>

      {/* MarkdownV2 with subject-name auto-bold (#80). attendeeFirstNames
          is threaded in from the parent (form-state attendeeMentions
          aren't in scope inside this sub-component). */}
      <MarkdownV2
        source={blueprint.blueprint_markdown}
        subjectNames={attendeeFirstNames || []}
      />

      <SectionHeader index="∗" title="Chat — quick-button presets" />

      <Callout.Tip>
        Tap a chip to ask a follow-up grounded in this Blueprint's underlying model. Tier-gated chips show locked with the required tier.
      </Callout.Tip>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "16px 0" }}>
        {(blueprint.quick_button_prompts || []).map((p, i) => {
          const locked = p.tier_required && !canAccess(tier, `pre_meeting_${p.tier_required}`);
          return (
            <Chip
              key={i}
              locked={locked}
              lockedTierLabel={locked ? tierDisplayLabel(p.tier_required) : undefined}
              onClick={() => !locked && onPreset(p.preset_text || p.label)}
            >
              {p.label}
            </Chip>
          );
        })}
      </div>

      <div style={{ marginTop: 24 }}>
        {chatTurns.map((t, i) => (
          <ChatTurn key={i} role={t.role} timestamp={t.ts ? t.ts.toLocaleTimeString() : null}>
            {t.content}
          </ChatTurn>
        ))}
        <Composer
          placeholder="Ask a follow-up about this meeting…"
          onSubmit={onMessage}
        />
      </div>

      <PostMeetingNotes
        blueprintId={blueprint.blueprint_id}
        attendeeProfileIds={attendeeProfileIds}
        bearerToken={bearerToken}
      />

      <AuditFooter
        engineVersion={blueprint.engine_version}
        jargonScanPassed={true}
        blueprintId={blueprint.blueprint_id}
        pageIndicator="page 1 of 1"
      />

      {/* Print-to-PDF (#73). */}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <PrintToPdfButton label="Save as PDF" />
      </div>
    </article>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
};

const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
};

function Field({ label, children }) {
  return (
    <div>
      <label
        style={{
          display: "block",
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          color: "var(--ppr-color-text-secondary)",
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
